"use client";
import { App, Form, FormProps } from "antd";
import { ReactNode, useRef, useState, createContext, useContext, useEffect } from "react";
import dayjs from "dayjs";

interface AppFormProps extends Omit<FormProps, "action"> {
    action?: {
        url: string,
        method?: string,
        headers?: Record<string, string>,
    };
    modifyParams?: (params: Record<string, unknown>) => Record<string, unknown>;
    onFormSuccess?: (data: any) => void;
    children?: ReactNode;
}

// List of field names that should be treated as dates
const DATE_FIELDS = ["date", "createdOn", "updatedOn", "startDate", "endDate", "dob"];

export default function AppForm({ children, initialValues, action, modifyParams, onFormSuccess, onFinish, ...props }: AppFormProps) {
    // Context-aware instance rather than the static `message` import (which
    // cannot read ConfigProvider and warns). Needs <App> in AntThemeProvider.
    const { message } = App.useApp();
    const [form] = Form.useForm();
    // Convert date strings in initialValues to dayjs objects
    const processedInitialValues = initialValues ? Object.entries(initialValues).reduce((acc, [key, value]) => {
        if (DATE_FIELDS.includes(key) && value && !dayjs.isDayjs(value)) {
            acc[key] = dayjs(value);
        } else {
            acc[key] = value;
        }
        return acc;
    }, {} as Record<string, any>) : undefined;

    const handleFormSubmit = async (values: any) => {
        // If action URL is provided, submit via API
        if (action) {
            let params = modifyParams ? modifyParams(values) : values;
            try {
                const res = await fetch(action.url, {
                    method: action.method || "POST",
                    headers: { "Content-Type": "application/json", ...(action.headers || {}) },
                    body: JSON.stringify({ ...params }),
                });
                const data = await res.json();
                if (!res.ok) {
                    throw new Error(data.error || "Failed to submit form");
                }
                message.success(data.message || "Form submitted successfully");
                form.resetFields();
                onFormSuccess?.(data.data);
            } catch (err) {
                console.error("Error submitting form:", err);
                message.error(err instanceof Error ? err.message : "Failed to submit form");
            }
        } else {
            // If no action URL, call the original onFinish
            onFinish?.(values);
        }
    };

    return (
        <Form
            {...props}
            form={form}
            initialValues={processedInitialValues}
            onFinish={handleFormSubmit}
        >
            {children}
        </Form>
    );
}
